/**
 * HipAbdmMenu - the slim "Connected to ABDM ▾" / "Not connected ▾" chip in the page header (redesign v2, OR-65).
 * Opening it shows the registration panel (HIP ID, status, Try again) and the "send ABHA app link by SMS" box.
 *
 * The panel and the SMS box stay MOUNTED while the menu is closed (just hidden): the panel fetches the
 * registration status once on mount and reports the mode through `onModeResolved`, so opening or closing the
 * menu never sends a request. The chip reads the same status from useAbdmModeStore (one shared copy).
 * Esc or a click outside closes it. Solid fills only (visible in dark mode); state = dot + words, not colour alone.
 */
import React, { useEffect, useRef, useState } from 'react';

import HipRegistrationPanel from './HipRegistrationPanel';
import SendAbhaLinkSms from './SendAbhaLinkSms';
import { usePermission } from '../../context/PermissionContext';
import { useTheme } from '../../theme/ThemeContext';
import useAbdmModeStore, { hipConnectionOf } from '../../stores/abdmModeStore';
import { ABDM_MENU } from '../../constants/hipRecordsCopy';

const MENU_ID = 'hip-abdm-menu';

export default function HipAbdmMenu({ onModeResolved }) {
  const { isDark } = useTheme();
  const canSms = usePermission('HIP_LINK_SMS');
  const reg = useAbdmModeStore((s) => s.reg);
  const ready = useAbdmModeStore((s) => s.ready);
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const chipRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      chipRef.current?.focus();
    };
    const onDown = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  let label = ABDM_MENU.checking;
  let dot = isDark ? '#9ca3af' : '#6b7280';
  if (reg || ready) {
    const state = reg ? hipConnectionOf(reg) : null;
    const ok = !!state && state.connected && !state.pending;
    label = ok ? ABDM_MENU.connected : ABDM_MENU.notConnected;
    dot = ok ? (isDark ? '#4ade80' : '#16a34a') : (isDark ? '#f87171' : '#dc2626');
  }

  const text = isDark ? '#f1f5f9' : '#111827';
  const muted = isDark ? '#9ca3af' : '#6b7280';
  const border = `1px solid ${isDark ? '#475569' : '#9ca3af'}`;
  const fill = isDark ? '#1e293b' : '#ffffff';

  return (
    <div ref={rootRef} style={{ position: 'relative', marginLeft: 'auto' }}>
      <button
        ref={chipRef}
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={MENU_ID}
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 7, padding: '6px 12px', borderRadius: 99,
          fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer',
          background: fill, color: text, border,
        }}
      >
        <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: '50%', background: dot, display: 'inline-block' }} />
        {label}
        <span aria-hidden="true" style={{ color: muted }}>▾</span>
      </button>
      <div
        id={MENU_ID}
        role="group"
        aria-label={ABDM_MENU.heading}
        hidden={!open}
        style={{
          position: 'absolute', zIndex: 40, top: 'calc(100% + 6px)', right: 0,
          width: 330, maxWidth: 'calc(100vw - 32px)', boxSizing: 'border-box', padding: 12,
          background: fill, color: text, border, borderRadius: 10, boxShadow: '0 10px 28px rgba(0,0,0,0.25)',
        }}
      >
        <p style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: muted }}>
          {ABDM_MENU.heading}
        </p>
        <HipRegistrationPanel compact onModeResolved={onModeResolved} />
        {canSms && (
          <>
            <hr style={{ border: 0, borderTop: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`, margin: '12px 0' }} />
            <p style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: muted }}>
              {ABDM_MENU.smsHeading}
            </p>
            <SendAbhaLinkSms />
          </>
        )}
      </div>
    </div>
  );
}
